import React, { useState } from 'react';
import { APP_NAME, APP_NAME_LOWER } from '../config/branding';
import './HelpSupportPage.css';

interface FAQItem {
    question: string;
    answer: string;
    category: string;
}

const HelpSupportPage: React.FC = () => {
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

    const faqData: FAQItem[] = [
        {
            question: `${APP_NAME_LOWER} nasıl çalışır?`,
            answer: `${APP_NAME_LOWER}, gelir ve giderlerinizi kategorilere ayırarak takip etmenizi sağlar. Varlıklarınızı ekleyebilir, bütçe hedefleri belirleyebilir ve finansal durumunuzu analiz edebilirsiniz.`,
            category: "general"
        },
        {
            question: "Hesap oluşturmak ücretsiz mi?",
            answer: `Evet, ${APP_NAME_LOWER} tamamen ücretsizdir. Herhangi bir ücret ödemeden tüm özellikleri kullanabilirsiniz.`,
            category: "general"
        },
        {
            question: "Verilerim güvenli mi?",
            answer: "Evet, tüm verileriniz SSL şifreleme ile korunur ve güvenli sunucularda saklanır. Kişisel bilgileriniz üçüncü taraflarla paylaşılmaz.",
            category: "security"
        },
        {
            question: "Gelir ve gider nasıl eklenir?",
            answer: "Ana ekranda '+' butonuna tıklayarak yeni işlem ekleyebilirsiniz. İşlem türünü (gelir/gider), miktarı, kategorisini ve tarihini belirleyin.",
            category: "usage"
        },
        {
            question: "Kategori nasıl oluşturulur?",
            answer: "Ayarlar menüsünden 'Kategoriler' bölümüne gidin ve 'Yeni Kategori' butonuna tıklayın. Kategori adını ve rengini belirleyebilirsiniz.",
            category: "usage"
        },
        {
            question: "Varlık nasıl eklenir?",
            answer: "Varlıklar ekranından 'Yeni Varlık' butonuna tıklayın. Varlık türünü (nakit, banka, yatırım), miktarını ve açıklamasını girin.",
            category: "usage"
        },
        {
            question: "Bütçe hedefi nasıl belirlenir?",
            answer: "Raporlar ekranından 'Hedefler' sekmesine gidin ve 'Yeni Hedef' butonuna tıklayın. Hedef miktarını ve süresini belirleyin.",
            category: "usage"
        },
        {
            question: "Verilerimi nasıl yedekleyebilirim?",
            answer: "Şu anda otomatik yedekleme özelliği bulunmaktadır. Verileriniz bulut sunucularında güvenli şekilde saklanır.",
            category: "data"
        },
        {
            question: "Uygulamayı nasıl güncelleyebilirim?",
            answer: "Google Play Store'dan uygulama güncellemelerini takip edebilirsiniz. Otomatik güncelleme de etkinleştirilebilir.",
            category: "technical"
        },
        {
            question: "Hangi cihazlarda çalışır?",
            answer: `${APP_NAME_LOWER} Android 6.0 ve üzeri işletim sistemine sahip tüm Android cihazlarda çalışır.`,
            category: "technical"
        }
    ];

    const categories = [
        { id: 'all', name: 'Tümü', icon: '📋' },
        { id: 'general', name: 'Genel', icon: '❓' },
        { id: 'usage', name: 'Kullanım', icon: '📱' },
        { id: 'security', name: 'Güvenlik', icon: '🔒' },
        { id: 'data', name: 'Veri', icon: '💾' },
        { id: 'technical', name: 'Teknik', icon: '⚙️' }
    ];

    const filteredFAQs = activeCategory === 'all'
        ? faqData
        : faqData.filter(faq => faq.category === activeCategory);

    const toggleFAQ = (index: number) => {
        setExpandedFAQ(expandedFAQ === index ? null : index);
    };

    return (
        <div className="help-support-page">
            <div className="container">
                <div className="page-header">
                    <h1>Yardım & Destek</h1>
                    <p>{APP_NAME} kullanımı hakkında sık sorulan sorular ve yardım kaynakları</p>
                </div>

                <div className="support-content">
                    {/* Quick Help Section */}
                    <section className="quick-help-section">
                        <h2>Hızlı Yardım</h2>
                        <div className="quick-help-grid">
                            <div className="help-card">
                                <div className="help-icon">📱</div>
                                <h3>Başlangıç Rehberi</h3>
                                <p>İlk kez kullanıyorsanız bu rehberi takip edin</p>
                                <a href="#getting-started" className="help-link">Rehberi Görüntüle →</a>
                            </div>

                            <div className="help-card">
                                <div className="help-icon">📚</div>
                                <h3>Kullanım Kılavuzu</h3>
                                <p>Tüm özelliklerin detaylı açıklaması</p>
                                <a href="#user-guide" className="help-link">Kılavuzu Görüntüle →</a>
                            </div>

                            <div className="help-card">
                                <div className="help-icon">🎥</div>
                                <h3>Video Eğitimler</h3>
                                <p>Görsel öğrenme için video içerikler</p>
                                <a href="#videos" className="help-link">Videoları Görüntüle →</a>
                            </div>

                            <div className="help-card">
                                <div className="help-icon">📧</div>
                                <h3>İletişim</h3>
                                <p>Doğrudan destek ekibiyle iletişim</p>
                                <a href="#contact" className="help-link">İletişime Geç →</a>
                            </div>
                        </div>
                    </section>

                    {/* FAQ Section */}
                    <section className="faq-section">
                        <h2>Sık Sorulan Sorular</h2>

                        <div className="category-filter">
                            {categories.map(category => (
                                <button
                                    key={category.id}
                                    className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(category.id)}
                                >
                                    <span className="category-icon">{category.icon}</span>
                                    {category.name}
                                </button>
                            ))}
                        </div>

                        <div className="faq-list">
                            {filteredFAQs.map((faq, index) => (
                                <div key={index} className="faq-item">
                                    <button
                                        className="faq-question"
                                        onClick={() => toggleFAQ(index)}
                                    >
                                        <span className="question-text">{faq.question}</span>
                                        <span className={`expand-icon ${expandedFAQ === index ? 'expanded' : ''}`}>
                                            {expandedFAQ === index ? '−' : '+'}
                                        </span>
                                    </button>
                                    {expandedFAQ === index && (
                                        <div className="faq-answer">
                                            <p>{faq.answer}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Getting Started Guide */}
                    <section id="getting-started" className="guide-section">
                        <h2>Başlangıç Rehberi</h2>
                        <div className="guide-steps">
                            <div className="guide-step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Uygulamayı İndirin</h3>
                                    <p>Google Play Store&apos;dan {APP_NAME_LOWER}&apos;i indirin ve cihazınıza kurun.</p>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Hesap Oluşturun</h3>
                                    <p>E-posta adresinizle hesap oluşturun ve giriş yapın.</p>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>Varlıklarınızı Ekleyin</h3>
                                    <p>Mevcut nakit, banka hesapları ve yatırımlarınızı sisteme girin.</p>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <h3>Kategoriler Oluşturun</h3>
                                    <p>Gelir ve gider kategorilerinizi tanımlayın (örn: maaş, market, ulaşım).</p>
                                </div>
                            </div>

                            <div className="guide-step">
                                <div className="step-number">5</div>
                                <div className="step-content">
                                    <h3>İşlemleri Kaydetmeye Başlayın</h3>
                                    <p>Gelir ve giderlerinizi kategorileriyle birlikte kaydedin.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Contact Section */}
                    <section id="contact" className="contact-section">
                        <h2>İletişim & Destek</h2>
                        <div className="contact-grid">
                            <div className="contact-card">
                                <div className="contact-icon">📧</div>
                                <h3>E-posta Desteği</h3>
                                <p>Teknik sorunlar ve genel sorular için</p>
                                <a href="mailto:support@budgetapp.com" className="contact-link">
                                    support@budgetapp.com
                                </a>
                            </div>

                            <div className="contact-card">
                                <div className="contact-icon">🔒</div>
                                <h3>Gizlilik & Güvenlik</h3>
                                <p>Gizlilik politikası ve güvenlik soruları için</p>
                                <a href="mailto:privacy@budgetapp.com" className="contact-link">
                                    privacy@budgetapp.com
                                </a>
                            </div>

                            <div className="contact-card">
                                <div className="contact-icon">💡</div>
                                <h3>Öneri & Geri Bildirim</h3>
                                <p>Uygulama geliştirme önerileri için</p>
                                <a href="mailto:feedback@budgetapp.com" className="contact-link">
                                    feedback@budgetapp.com
                                </a>
                            </div>
                        </div>

                        <div className="response-time">
                            <h3>Yanıt Süreleri</h3>
                            <ul>
                                <li><strong>E-posta Desteği:</strong> 24 saat içinde</li>
                                <li><strong>Teknik Sorunlar:</strong> 48 saat içinde</li>
                                <li><strong>Öneri & Geri Bildirim:</strong> 1 hafta içinde</li>
                            </ul>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default HelpSupportPage;
